import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { usePixel } from './src/hooks/usePixel';

// Layouts (keep eager loaded as they're needed immediately)
import StoreLayout from './src/layouts/StoreLayout';
import AdminLayout from './src/layouts/AdminLayout';

// Contexts and Components (keep eager loaded)
import { AuthProvider } from './src/contexts/AuthContext';
import { CartProvider } from './src/contexts/CartContext';
import ProtectedRoute from './src/components/ProtectedRoute';
import AffiliateTracker from './src/components/AffiliateTracker';
import { AnalyticsProvider } from './src/contexts/AnalyticsContext';
import { ToastProvider } from './src/contexts/ToastContext';

// Lazy load all pages for better performance
// Store Pages - exported from index.tsx
const HomePage = lazy(() => import('./src/pages/store').then((m) => ({ default: m.HomePage })));
const CatalogPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.CatalogPage }))
);
const AboutPage = lazy(() => import('./src/pages/store').then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.ContactPage }))
);
const ExchangesPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.ExchangesPage }))
);
const ResellerPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.ResellerPage }))
);
const TrackingPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.TrackingPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.PrivacyPolicyPage }))
);
const CatalogVerazPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.CatalogVerazPage }))
);
const ProductPage = lazy(() =>
  import('./src/pages/store').then((m) => ({ default: m.ProductPage }))
);

const CustomPage = lazy(() => import('./src/pages/store/CustomPage'));
const CartPage = lazy(() => import('./src/pages/store/CartPage'));
const CheckoutPage = lazy(() => import('./src/pages/store/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./src/pages/store/CheckoutSuccessPage'));
const CheckoutFailurePage = lazy(() => import('./src/pages/store/CheckoutFailurePage'));
const StoreLogin = lazy(() => import('./src/pages/store/Login'));
const StoreRegister = lazy(() => import('./src/pages/store/Register'));
const CustomerDashboard = lazy(() => import('./src/pages/store/CustomerDashboard'));
const BlogPage = lazy(() => import('./src/pages/store/BlogPage'));
const BlogPostPage = lazy(() => import('./src/pages/store/BlogPostPage'));
const KitsPage = lazy(() => import('./src/pages/store/KitsPage'));
const EquipmentPage = lazy(() => import('./src/pages/store/EquipmentPage'));

// Admin Pages
const AnalyticsDashboard = lazy(() => import('./src/pages/admin/AnalyticsDashboard'));
const Products = lazy(() => import('./src/pages/admin/Products'));
const ProductForm = lazy(() => import('./src/pages/admin/ProductForm'));
const Categories = lazy(() => import('./src/pages/admin/Categories'));
const CategoryForm = lazy(() => import('./src/pages/admin/CategoryForm'));
const Coupons = lazy(() => import('./src/pages/admin/Coupons'));
const CouponForm = lazy(() => import('./src/pages/admin/CouponForm'));
const Customers = lazy(() => import('./src/pages/admin/Customers'));

const Sales = lazy(() => import('./src/pages/admin/Sales'));
const Settings = lazy(() => import('./src/pages/admin/Settings'));
const Users = lazy(() => import('./src/pages/admin/Users'));
const MenuManager = lazy(() => import('./src/pages/admin/MenuManager'));
const PageManager = lazy(() => import('./src/pages/admin/PageManager'));
const PageEditor = lazy(() => import('./src/pages/admin/PageEditor'));
const Affiliates = lazy(() => import('./src/pages/admin/Affiliates'));
const Kits = lazy(() => import('./src/pages/admin/Kits'));
const PermissionsPage = lazy(() => import('./src/pages/admin/Permissions'));
const BlogAdmin = lazy(() => import('./src/pages/admin/Blog'));
const BlogPostForm = lazy(() => import('./src/pages/admin/BlogPostForm'));
const Login = lazy(() => import('./src/pages/admin/Login'));
const AiUpdate = lazy(() => import('./src/pages/admin/AiUpdate'));
const CloudinaryMigration = lazy(() => import('./src/pages/admin/CloudinaryMigration'));
const StoreLayoutSettings = lazy(() => import('./src/pages/admin/StoreLayout'));
const Reviews = lazy(() => import('./src/pages/admin/Reviews'));
const Questions = lazy(() => import('./src/pages/admin/Questions'));
const NewsletterAdmin = lazy(() => import('./src/pages/admin/Newsletter'));
const StoriesMedia = lazy(() => import('./src/pages/admin/StoriesMedia'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-veraz-pink"></div>
  </div>
);

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const PixelTracker = () => {
  usePixel();
  return null;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CartProvider>
        <ToastProvider>
          <Router>
            <PixelTracker />
            <AnalyticsProvider>
              <Analytics />
              <ScrollToTop />
              <AffiliateTracker />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Store Routes */}
                  <Route path="/" element={<StoreLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="produtos" element={<CatalogPage />} />
                    <Route path="produtos/:category" element={<CatalogPage />} />
                    <Route path="sobre" element={<AboutPage />} />
                    <Route path="contato" element={<ContactPage />} />
                    <Route path="trocas" element={<ExchangesPage />} />
                    <Route path="seja-um-revendedor" element={<ResellerPage />} />
                    <Route path="rastreio" element={<TrackingPage />} />
                    <Route path="politica-de-privacidade" element={<PrivacyPolicyPage />} />
                    <Route path="catalogoveraz" element={<CatalogVerazPage />} />
                    <Route path="produto/:id" element={<ProductPage />} />
                    <Route path="paginas/:slug" element={<CustomPage />} />

                    <Route path="carrinho" element={<CartPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />
                    <Route path="checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="checkout/failure" element={<CheckoutFailurePage />} />
                    <Route path="login" element={<StoreLogin />} />
                    <Route path="cadastro" element={<StoreRegister />} />
                    <Route path="minha-conta" element={<CustomerDashboard />} />
                    <Route path="blog" element={<BlogPage />} />
                    <Route path="blog/:slug" element={<BlogPostPage />} />
                    <Route path="equipamentos" element={<EquipmentPage />} />
                    <Route path="kits" element={<KitsPage />} />
                  </Route>

                  {/* Admin Login */}
                  <Route path="/admin/login" element={<Login />} />

                  {/* Legacy catalog redirects */}
                  <Route path="/catalogo2026" element={<Navigate to="/catalogoveraz" replace />} />
                  <Route path="/catalogo2026v2" element={<Navigate to="/catalogoveraz" replace />} />
                  <Route
                    path="/promo/descartaveis"
                    element={<Navigate to="/catalogoveraz" replace />}
                  />

                  {/* Admin Routes (Protected) */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AnalyticsDashboard />} />
                    <Route path="dashboard" element={<Navigate to="/admin" replace />} />
                    <Route
                      path="products"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <Products />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="products/new"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <ProductForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="products/edit/:id"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <ProductForm />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="kits"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <Kits />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="categories"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <Categories />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="categories/new"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <CategoryForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="categories/edit/:id"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <CategoryForm />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="coupons"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <Coupons />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="coupons/new"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <CouponForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="coupons/edit/:id"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <CouponForm />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="customers"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'viewer']}>
                          <Customers />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="newsletter"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <NewsletterAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="stories-media"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <StoriesMedia />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="sales"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'viewer']}>
                          <Sales />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="menus"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <MenuManager />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="pages"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <PageManager />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="pages/new"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <PageEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="pages/edit/:id"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <PageEditor />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="settings"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="store-layout"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <StoreLayoutSettings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="reviews"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <Reviews />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="questions"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <Questions />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="users"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <Users />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="permissions"
                      element={
                        <ProtectedRoute allowedRoles={['master']}>
                          <PermissionsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="affiliates"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <Affiliates />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="blog"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <BlogAdmin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="blog/:id"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin', 'editor']}>
                          <BlogPostForm />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="ai-update"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <AiUpdate />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="cloudinary-migration"
                      element={
                        <ProtectedRoute allowedRoles={['master', 'admin']}>
                          <CloudinaryMigration />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AnalyticsProvider>
          </Router>
        </ToastProvider>
      </CartProvider>
    </AuthProvider>
  );
};

export default App;
